import type * as Monaco from 'monaco-editor/esm/vs/editor/editor.api.js';

/** Monarch grammar for Rhai, the Prism engine's scripting language (*.rhai). */
export function registerRhai(monaco: typeof Monaco): void {
  if (monaco.languages.getLanguages().some((l) => l.id === 'rhai')) return;
  monaco.languages.register({ id: 'rhai', extensions: ['.rhai'], aliases: ['Rhai', 'rhai'] });

  monaco.languages.setLanguageConfiguration('rhai', {
    comments: { lineComment: '//', blockComment: ['/*', '*/'] },
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"', notIn: ['string'] },
      { open: '`', close: '`', notIn: ['string'] },
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    indentationRules: {
      increaseIndentPattern: /^.*\{[^}"']*$/,
      decreaseIndentPattern: /^\s*\}/,
    },
  });

  monaco.languages.setMonarchTokensProvider('rhai', {
    defaultToken: '',
    keywords: [
      'let', 'const', 'fn', 'private', 'if', 'else', 'switch', 'while', 'loop', 'do', 'until',
      'for', 'in', 'break', 'continue', 'return', 'throw', 'try', 'catch', 'import', 'export',
      'as', 'global', 'is_shared', 'Fn', 'call', 'curry', 'type_of', 'print', 'debug', 'eval',
    ],
    constants: ['true', 'false', 'this', '()'],
    // Prism engine API modules bound by prism_script.
    builtins: [
      'entity', 'transform', 'input', 'physics', 'scene', 'time', 'ui', 'audio', 'terrain',
      'save', 'data', 'log', 'props',
      // Procedural generators (prism_script api/procgen.rs, generators.md 11.7)
      'procgen_value', 'procgen_weights', 'procgen_eval', 'procgen_points', 'procgen_nearest',
      'procgen_area_at', 'procgen_distance', 'procgen_param', 'procgen_ready', 'procgen_require',
      'procgen_remove', 'procgen_is_removed', 'procgen_removed', 'procgen_set_removed',
      'procgen_regenerate', 'procgen_info',
    ],
    callbacks: [
      'on_start', 'on_update', 'on_fixed_update', 'on_collision', 'on_trigger_enter', 'on_ui_event',
      'on_late_update', 'on_collision_stay', 'on_collision_exit', 'on_trigger_stay', 'on_trigger_exit',
      'on_event', 'on_destroy',
      // Procedural generator scripts (generators.md 9.2)
      'generate', 'fill',
    ],
    operators: [
      '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=', '&&', '||', '++', '--', '+',
      '-', '*', '/', '&', '|', '^', '%', '<<', '>>', '+=', '-=', '*=', '/=', '&=', '|=', '^=',
      '%=', '<<=', '>>=', '**', '=>', '..', '..=', '?.', '??',
    ],
    symbols: /[=><!~?:&|+\-*/^%.]+/,
    escapes: /\\(?:[abfnrtv\\"'`]|x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
    tokenizer: {
      root: [
        [/(fn)(\s+)([a-zA-Z_]\w*)/, ['keyword', '', 'function']],
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              '@keywords': 'keyword',
              '@constants': 'constant',
              '@builtins': 'type',
              '@callbacks': 'function',
              '@default': 'identifier',
            },
          },
        ],
        { include: '@whitespace' },
        [/[{}()[\]]/, '@brackets'],
        [/@symbols/, { cases: { '@operators': 'operator', '@default': '' } }],
        [/0x[0-9a-fA-F_]+/, 'number.hex'],
        [/0b[01_]+/, 'number.binary'],
        [/\d[\d_]*\.\d[\d_]*([eE][-+]?\d+)?/, 'number.float'],
        [/\d[\d_]*/, 'number'],
        [/[;,.]/, 'delimiter'],
        [/"/, 'string', '@string'],
        [/`/, 'string', '@interpolated'],
        [/'[^\\']'/, 'string'],
        [/'(@escapes)'/, 'string'],
      ],
      whitespace: [
        [/[ \t\r\n]+/, ''],
        [/\/\*/, 'comment', '@comment'],
        [/\/\/.*$/, 'comment'],
      ],
      comment: [
        [/[^/*]+/, 'comment'],
        [/\*\//, 'comment', '@pop'],
        [/[/*]/, 'comment'],
      ],
      string: [
        [/[^\\"]+/, 'string'],
        [/@escapes/, 'string.escape'],
        [/\\./, 'string.escape.invalid'],
        [/"/, 'string', '@pop'],
      ],
      interpolated: [
        [/\$\{/, { token: 'delimiter.bracket', next: '@interpolation' }],
        [/[^\\`$]+/, 'string'],
        [/@escapes/, 'string.escape'],
        [/`/, 'string', '@pop'],
        [/./, 'string'],
      ],
      interpolation: [
        [/\}/, { token: 'delimiter.bracket', next: '@pop' }],
        { include: 'root' },
      ],
    },
  } as Monaco.languages.IMonarchLanguage);
}
